/**
 * @file quantity-with-free.tsx
 * @module app/modules/orders-delivery/components/order-details/quantity-with-free
 * @description Renders an order quantity with its Buy X Get Y free units, respecting role visibility.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 2.1.2
 * @version 2.1.2
 */

import {
  QuantityTypeWithOffer,
  getFreeQuantity,
  getOfferLabel,
  isBuyXGetYActive,
} from "../../utils/free-quantity";

interface QuantityWithFreeProps {
  paid: number;
  free?: number;
  unitLabel: string;
  showBreakdown: boolean;
  offerLabel?: string;
}

const QuantityWithFree = ({
  paid,
  free = 0,
  unitLabel,
  showBreakdown,
  offerLabel,
}: QuantityWithFreeProps) => {
  const freeUnits = Math.max(0, Number(free) || 0);
  const paidUnits = Math.max(0, Number(paid) || 0);
  if (!freeUnits) {
    return (
      <span className="fs-15 fw-600">
        {paidUnits} {unitLabel}
      </span>
    );
  }
  if (!showBreakdown) {
    return (
      <span className="fs-15 fw-600">
        {paidUnits + freeUnits} {unitLabel}
      </span>
    );
  }
  return (
    <span className="fs-15 fw-600 d-flex flex-column">
      <span>
        {paidUnits} {unitLabel} purchased
      </span>
      <span className="text-success">
        + {freeUnits} {unitLabel} free (TSh 0)
        {offerLabel ? ` · ${offerLabel}` : ''}
      </span>
      <span className="text-muted fs-13">
        Total: {paidUnits + freeUnits} {unitLabel}
      </span>
    </span>
  );
};

export default QuantityWithFree;

interface FreeQuantityHintProps {
  quantityType?: QuantityTypeWithOffer;
  paid: number | string;
  showTotal?: boolean;
  unitLabel?: string;
  /** Saved order line: honour the captured offer even if it has since expired. */
  isOrderSnapshot?: boolean;
}

/** Live preview while entering quantities; the server recalculates on save. */
export const FreeQuantityHint = ({
  quantityType,
  paid,
  showTotal = false,
  unitLabel = '',
  isOrderSnapshot = false,
}: FreeQuantityHintProps) => {
  const applies = isOrderSnapshot
    ? !!quantityType?.buyXGetYEnabled
    : isBuyXGetYActive(quantityType);
  if (!applies) return null;
  const label = getOfferLabel(quantityType, paid, undefined, isOrderSnapshot);
  const free = getFreeQuantity(quantityType, paid, undefined, isOrderSnapshot);
  if (free <= 0 || !label) return null;
  const total = Math.max(0, Math.floor(Number(paid) || 0)) + free;
  return (
    <span className="fs-13 fw-600 text-success d-block mt-1">
      + {free} free · {label}
      {showTotal && (
        <span className="d-block text-muted">
          Total: {total}{unitLabel ? ` ${unitLabel}` : ''}
        </span>
      )}
    </span>
  );
};
